/**
 * JourneyHeader — DRR-309 Phase 2, Phase 8
 *
 * Displays subject name + goal name with status indicators.
 * All labels come from backend — zero hardcoded domain terms.
 *
 * Phase 8: Responsive breakpoints for mobile/tablet/desktop
 */

import React from 'react';
import {
  Group,
  Stack,
  Text,
  Badge,
  ThemeIcon,
  Paper,
  Tooltip,
  Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconUser,
  IconPlayerPlay,
  IconPlayerPause,
  IconCheck,
  IconX,
  IconClock,
} from '@tabler/icons-react';
import type { JourneyHeaderProps } from '../types';

const STATUS_CONFIG: Record<
  string,
  { color: string; icon: React.ReactNode; label: string }
> = {
  PENDING: { color: 'gray', icon: <IconClock size={14} />, label: 'Pending' },
  RUNNING: { color: 'blue', icon: <IconPlayerPlay size={14} />, label: 'Active' },
  PAUSED: { color: 'yellow', icon: <IconPlayerPause size={14} />, label: 'Paused' },
  COMPLETED: { color: 'green', icon: <IconCheck size={14} />, label: 'Complete' },
  FAILED: { color: 'red', icon: <IconX size={14} />, label: 'Failed' },
  CANCELLED: { color: 'gray', icon: <IconX size={14} />, label: 'Cancelled' },
};

export function JourneyHeader({
  subjectName,
  goalName,
  goalDescription,
  status,
  dayNumber,
  startedAt,
  completedAt,
  compact = false,
}: JourneyHeaderProps) {
  // Phase 8: Responsive breakpoints
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const statusConfig = STATUS_CONFIG[status.toUpperCase()] ?? {
    color: 'gray',
    icon: <IconClock size={14} />,
    label: status,
  };

  if (compact) {
    return (
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="sm" variant="light" color="blue">
          <IconUser size={14} />
        </ThemeIcon>
        <Text size="sm" fw={500} truncate>
          {subjectName}'s {goalName}
        </Text>
        <Badge size="xs" variant="light" color={statusConfig.color}>
          Day {dayNumber}
        </Badge>
      </Group>
    );
  }

  // Mobile layout: stacked
  if (isMobile) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="xs">
          {/* Title row */}
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon size="md" variant="light" color="blue" radius="xl">
              <IconUser size={18} />
            </ThemeIcon>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="md" truncate>
                {subjectName}'s {goalName}
              </Text>
            </Box>
          </Group>

          {/* Status row */}
          <Group gap="xs" justify="space-between">
            <Badge
              size="sm"
              variant="light"
              color={statusConfig.color}
              leftSection={statusConfig.icon}
            >
              {statusConfig.label}
            </Badge>
            <Badge size="xs" variant="outline" color="gray">
              Day {dayNumber}
            </Badge>
          </Group>
        </Stack>
      </Paper>
    );
  }

  // Tablet layout: slightly compressed
  if (isTablet) {
    return (
      <Paper p="sm" withBorder>
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size="lg" variant="light" color="blue" radius="xl">
              <IconUser size={20} />
            </ThemeIcon>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="lg" truncate>
                {subjectName}'s {goalName}
              </Text>
              {goalDescription && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {goalDescription}
                </Text>
              )}
            </Box>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Badge
              size="md"
              variant="light"
              color={statusConfig.color}
              leftSection={statusConfig.icon}
            >
              {statusConfig.label}
            </Badge>
            <Badge size="sm" variant="outline" color="gray">
              Day {dayNumber}
            </Badge>
          </Group>
        </Group>
      </Paper>
    );
  }

  // Desktop layout: full
  return (
    <Paper p="md" withBorder>
      <Stack gap="xs">
        {/* Main title */}
        <Group justify="space-between" align="flex-start">
          <Group gap="md" wrap="nowrap">
            <ThemeIcon size="xl" variant="light" color="blue" radius="xl">
              <IconUser size={24} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="xl">
                {subjectName}'s {goalName}
              </Text>
              {goalDescription && (
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {goalDescription}
                </Text>
              )}
            </div>
          </Group>

          {/* Status badge and day counter */}
          <Stack align="flex-end" gap={4}>
            <Badge
              size="lg"
              variant="light"
              color={statusConfig.color}
              leftSection={statusConfig.icon}
            >
              {statusConfig.label}
            </Badge>
            <Tooltip
              label={`Started: ${startedAt.toLocaleDateString()}${
                completedAt ? ` • Completed: ${completedAt.toLocaleDateString()}` : ''
              }`}
            >
              <Badge size="sm" variant="outline" color="gray">
                Day {dayNumber}
              </Badge>
            </Tooltip>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
}

export default JourneyHeader;
